// Admin console: user + site + study management, ported from renderAdminView/
// openAddUserModal/handleAdminAction (source lines 3610-3812). An admin can
// optionally set a new user's initial/default password (POST /users'
// `password` field); mustChangePassword still forces a change at first
// login either way (see docs/DECISIONS.md).
import { usersApi, sitesApi, studiesApi } from "./api-client.js";
import { escapeHtml, fmtDate, openModal, closeModal, notify, $, $$ } from "./dom.js";
import { isAdmin, canImportData } from "./state.js";
import { ROLES, SPECIES_OPTIONS } from "@woof/shared";
import { renderImportSection } from "./admin-import-view.js";
import { renderNotificationSettingsSection } from "./admin-notifications-view.js";

function speciesLabel(species: string): string {
  return SPECIES_OPTIONS.find((s) => s.key === species)?.label ?? species;
}

export async function renderAdminView(root: HTMLElement): Promise<void> {
  if (!canImportData()) {
    root.innerHTML = `<div class="empty-state"><h3>Accès refusé</h3></div>`;
    return;
  }
  // Sponsor-class roles reach this page for the import section only - the
  // user/site/study management below stays admin-only, per the ADMIN-vs-
  // SPONSOR access split decided for the spreadsheet import feature.
  if (!isAdmin()) {
    root.innerHTML = `<h2 style="margin-bottom:16px">⚙️ Administration</h2>
      <div id="adminNotificationsSection"></div>
      <div id="adminImportSection"></div>`;
    renderNotificationSettingsSection($("#adminNotificationsSection")!);
    renderImportSection($("#adminImportSection")!, undefined);
    return;
  }
  root.innerHTML = `<div class="loading">Chargement…</div>`;
  const [users, sites, studies] = await Promise.all([usersApi.list(), sitesApi.list(), studiesApi.list()]);

  root.innerHTML = `
    <h2 style="margin-bottom:16px">⚙️ Administration</h2>
    <div class="section">
      <div class="section-header"><span class="title">👥 Utilisateurs (${users.length})</span><button class="btn btn-primary btn-sm" id="btnAddUser">➕ Ajouter</button></div>
      <div class="section-body">
        <table class="tbl">
          <thead><tr><th>Identifiant</th><th>Nom complet</th><th>Email</th><th>Rôle</th><th>Sites</th><th>Études</th><th>Statut</th><th>Dernière connexion</th><th>Actions</th></tr></thead>
          <tbody>
            ${users
              .map(
                (u: any) => `<tr>
              <td><strong>${escapeHtml(u.username)}</strong>${u.mustChangePassword ? ' <span class="badge badge-warning">PWD à changer</span>' : ""}</td>
              <td>${escapeHtml(u.fullName || "—")}</td>
              <td>${escapeHtml(u.email || "—")}</td>
              <td>${escapeHtml(ROLES[u.role]?.label ?? u.role)}</td>
              <td>${escapeHtml((u.sites || []).join(", ") || "—")}</td>
              <td>${escapeHtml((u.studyIds || []).map((id: number) => studies.find((s: any) => s.id === id)?.name ?? id).join(", ") || "—")}</td>
              <td>${u.active ? '<span class="badge badge-success">Actif</span>' : '<span class="badge badge-neutral">Désactivé</span>'}</td>
              <td>${fmtDate(u.lastLogin)}</td>
              <td>
                <button class="btn btn-xs btn-secondary" data-act="editUser" data-uid="${u.id}">Éditer</button>
                ${u.active && u.username !== "admin" ? `<button class="btn btn-xs btn-danger" data-act="deactivate" data-uid="${u.id}">Désactiver</button>` : ""}
                <button class="btn btn-xs btn-secondary" data-act="reset" data-uid="${u.id}">Reset PWD</button>
              </td>
            </tr>`
              )
              .join("")}
          </tbody>
        </table>
      </div>
    </div>

    <div class="section">
      <div class="section-header"><span class="title">🏢 Sites investigateurs (${sites.length})</span><button class="btn btn-primary btn-sm" id="btnAddSite">➕ Ajouter site</button></div>
      <div class="section-body">
        <table class="tbl">
          <thead><tr><th>Code</th><th>Nom</th><th>Pays</th><th>Statut</th><th>Actions</th></tr></thead>
          <tbody>
            ${sites
              .map(
                (s: any) => `<tr>
              <td><strong>${s.code}</strong></td><td>${escapeHtml(s.name)}</td><td>${s.country}</td>
              <td>${s.active ? '<span class="badge badge-success">Actif</span>' : '<span class="badge badge-neutral">Inactif</span>'}</td>
              <td><button class="btn btn-xs btn-secondary" data-act="${s.active ? "deactSite" : "actSite"}" data-code="${s.code}">${s.active ? "Désactiver" : "Activer"}</button></td>
            </tr>`
              )
              .join("")}
          </tbody>
        </table>
      </div>
    </div>

    <div class="section">
      <div class="section-header"><span class="title">🧪 Études cliniques (${studies.length})</span><button class="btn btn-primary btn-sm" id="btnAddStudy">➕ Ajouter étude</button></div>
      <div class="section-body">
        <table class="tbl">
          <thead><tr><th>Nom</th><th>Espèce</th><th>Description</th><th>Sites</th><th>Patients</th><th>Actions</th></tr></thead>
          <tbody>
            ${studies
              .map(
                (s: any) => `<tr>
              <td><strong>${escapeHtml(s.name)}</strong></td>
              <td>${escapeHtml(speciesLabel(s.species))}</td>
              <td>${escapeHtml(s.description || "—")}</td>
              <td>${escapeHtml((s.siteCodes || []).join(", ") || "—")}</td>
              <td>${s.patientCount}</td>
              <td>
                <button class="btn btn-xs btn-secondary" data-act="editStudy" data-sid="${s.id}">Éditer</button>
                <button class="btn btn-xs btn-danger" data-act="deleteStudy" data-sid="${s.id}">Supprimer</button>
              </td>
            </tr>`
              )
              .join("")}
          </tbody>
        </table>
        ${studies.length === 0 ? '<p class="field-hint" style="padding:8px 0">Aucune étude pour le moment.</p>' : ""}
      </div>
    </div>
    <div id="adminNotificationsSection"></div>
    <div id="adminImportSection"></div>`;

  $("#btnAddUser")!.addEventListener("click", () => openUserModal(sites, studies, root));
  $("#btnAddSite")!.addEventListener("click", () => openAddSiteModal(root));
  $("#btnAddStudy")!.addEventListener("click", () => openStudyModal(sites, root));
  renderNotificationSettingsSection($("#adminNotificationsSection")!);
  renderImportSection($("#adminImportSection")!, sites);

  $$("button[data-act]", root).forEach((b) => {
    const act = b.getAttribute("data-act");
    if (act === "deactSite" || act === "actSite") {
      b.addEventListener("click", async () => {
        const code = b.getAttribute("data-code")!;
        if (act === "deactSite" && !confirm(`Désactiver le site ${code} ?`)) return;
        await sitesApi.setActive(code, act === "actSite");
        void renderAdminView(root);
      });
    } else if (act === "editUser") {
      b.addEventListener("click", () => {
        const uid = b.getAttribute("data-uid")!;
        const user = users.find((u: any) => u.id === uid);
        if (user) openUserModal(sites, studies, root, user);
      });
    } else if (act === "editStudy") {
      b.addEventListener("click", () => {
        const sid = b.getAttribute("data-sid")!;
        const study = studies.find((s: any) => String(s.id) === sid);
        if (study) openStudyModal(sites, root, study);
      });
    } else if (act === "deleteStudy") {
      b.addEventListener("click", async () => {
        const sid = b.getAttribute("data-sid")!;
        const study = studies.find((s: any) => String(s.id) === sid);
        if (!confirm(`Supprimer l'étude "${study?.name ?? sid}" ? Les patients associés ne seront pas supprimés.`)) return;
        await studiesApi.remove(sid);
        void renderAdminView(root);
      });
    } else {
      b.addEventListener("click", async () => {
        const uid = b.getAttribute("data-uid")!;
        if (act === "deactivate") {
          const reason = prompt("Raison de la désactivation :");
          if (!reason) return;
          await usersApi.deactivate(uid, reason);
          void renderAdminView(root);
        } else if (act === "reset") {
          if (!confirm("Réinitialiser le mot de passe de cet utilisateur ?")) return;
          await usersApi.resetPassword(uid);
          notify("success", "Mot de passe réinitialisé. L'utilisateur devra en définir un nouveau à sa prochaine connexion.");
        }
      });
    }
  });
}

function openUserModal(sites: any[], studies: any[], root: HTMLElement, user?: any): void {
  const isEdit = !!user;
  const userSites: string[] = user?.sites ?? [];
  const userStudies: number[] = user?.studyIds ?? [];
  const roleOpts = Object.values(ROLES)
    .map((r) => `<option value="${r.code}" ${user?.role === r.code ? "selected" : ""}>${r.label}</option>`)
    .join("");
  // Unchecked by default for a new user, same as for an edit - "select all"
  // silently granted every site/study that existed at creation time,
  // invisible until a second site/study actually existed to leak access to.
  // An admin now has to deliberately grant each one.
  const sitesHtml = sites
    .map(
      (s) =>
        `<label style="display:inline-flex;align-items:center;gap:4px;margin-right:12px"><input type="checkbox" class="site-cb" value="${s.code}" ${
          userSites.includes(s.code) ? "checked" : ""
        }> ${s.code} — ${escapeHtml(s.name)}</label>`
    )
    .join("");
  const studiesHtml = studies
    .map(
      (s) =>
        `<label style="display:inline-flex;align-items:center;gap:4px;margin-right:12px"><input type="checkbox" class="study-cb" value="${s.id}" ${
          userStudies.includes(s.id) ? "checked" : ""
        }> ${escapeHtml(s.name)}</label>`
    )
    .join("");
  openModal(
    isEdit ? `Éditer l'utilisateur — ${escapeHtml(user.username)}` : "Ajouter un utilisateur",
    `<div class="form-grid cols-2">
      <div class="form-group"><label>Identifiant ${isEdit ? "" : '<span class="required-mark">*</span>'}</label><input type="text" id="au_user" autocomplete="off" value="${isEdit ? escapeHtml(user.username) : ""}" ${isEdit ? "disabled" : ""}></div>
      <div class="form-group"><label>Nom complet</label><input type="text" id="au_name" value="${isEdit ? escapeHtml(user.fullName || "") : ""}"></div>
      <div class="form-group"><label>Email</label><input type="email" id="au_email" value="${isEdit ? escapeHtml(user.email || "") : ""}"></div>
      <div class="form-group"><label>Rôle <span class="required-mark">*</span></label><select id="au_role">${roleOpts}</select></div>
      ${isEdit ? "" : `<div class="form-group"><label>Mot de passe initial (facultatif)</label><input type="password" id="au_pwd" autocomplete="new-password"></div>`}
      <div class="form-group" style="grid-column:1/-1"><label>Sites</label><div>${sitesHtml}</div></div>
      <div class="form-group" style="grid-column:1/-1"><label>Études</label><div>${studiesHtml || '<span class="muted">Aucune étude définie.</span>'}</div></div>
    </div>
    <p class="field-hint">${isEdit ? "Utilisez « Reset PWD » dans la liste pour réinitialiser le mot de passe." : "Si aucun mot de passe n'est défini, l'utilisateur définira le sien à sa première connexion. Dans tous les cas, un changement de mot de passe sera exigé à la première connexion."}</p>`,
    `<button class="btn btn-secondary" id="auCancel">Annuler</button><button class="btn btn-primary" id="auSave">${isEdit ? "Enregistrer" : "Créer"}</button>`
  );
  $("#auCancel")!.addEventListener("click", closeModal);
  $("#auSave")!.addEventListener("click", async () => {
    const role = ($("#au_role") as HTMLSelectElement).value;
    const selectedSites = $$<HTMLInputElement>(".site-cb:checked").map((cb) => cb.value);
    const selectedStudies = $$<HTMLInputElement>(".study-cb:checked").map((cb) => Number(cb.value));
    const fullName = ($("#au_name") as HTMLInputElement).value.trim();
    const email = ($("#au_email") as HTMLInputElement).value.trim() || undefined;
    try {
      if (isEdit) {
        await usersApi.update(user.id, { role, sites: selectedSites, studyIds: selectedStudies, fullName, email });
      } else {
        const username = ($("#au_user") as HTMLInputElement).value.trim();
        if (!username || !role) return notify("error", "Identifiant et rôle sont requis");
        const password = ($("#au_pwd") as HTMLInputElement).value || undefined;
        await usersApi.create({ username, role, sites: selectedSites, studyIds: selectedStudies, fullName, email, password });
      }
    } catch (err) {
      notify("error", err instanceof Error ? err.message : "Erreur");
      return;
    }
    closeModal();
    void renderAdminView(root);
  });
}

function openAddSiteModal(root: HTMLElement): void {
  openModal(
    "Ajouter un site",
    `<div class="form-grid">
      <div class="form-group"><label>Code <span class="required-mark">*</span></label><input type="text" id="as_code" maxlength="10"></div>
      <div class="form-group"><label>Nom <span class="required-mark">*</span></label><input type="text" id="as_name"></div>
      <div class="form-group"><label>Pays</label><input type="text" id="as_country" value="FR" maxlength="2"></div>
    </div>`,
    `<button class="btn btn-secondary" id="asCancel">Annuler</button><button class="btn btn-primary" id="asSave">Créer</button>`
  );
  $("#asCancel")!.addEventListener("click", closeModal);
  $("#asSave")!.addEventListener("click", async () => {
    const code = ($("#as_code") as HTMLInputElement).value.trim();
    const name = ($("#as_name") as HTMLInputElement).value.trim();
    if (!code || !name) return notify("error", "Code et nom sont requis");
    try {
      await sitesApi.create({ code, name, country: ($("#as_country") as HTMLInputElement).value.trim() || "FR" });
    } catch (err) {
      notify("error", err instanceof Error ? err.message : "Erreur");
      return;
    }
    closeModal();
    void renderAdminView(root);
  });
}

function openStudyModal(sites: any[], root: HTMLElement, study?: any): void {
  const isEdit = !!study;
  const selectedCodes: string[] = study?.siteCodes ?? [];
  const sitesHtml = sites
    .map(
      (s) =>
        `<label style="display:inline-flex;align-items:center;gap:4px;margin-right:12px"><input type="checkbox" class="study-site-cb" value="${s.code}" ${selectedCodes.includes(s.code) ? "checked" : ""}> ${s.code} — ${escapeHtml(s.name)}</label>`
    )
    .join("");
  const speciesOpts = SPECIES_OPTIONS.map((s) => `<option value="${s.key}" ${study?.species === s.key ? "selected" : ""}>${s.label}</option>`).join("");
  openModal(
    isEdit ? `Éditer l'étude — ${escapeHtml(study.name)}` : "Ajouter une étude",
    `<div class="form-grid">
      <div class="form-group"><label>Nom <span class="required-mark">*</span></label><input type="text" id="st_name" value="${isEdit ? escapeHtml(study.name) : ""}"></div>
      <div class="form-group"><label>Espèce <span class="required-mark">*</span></label><select id="st_species">${speciesOpts}</select></div>
      <div class="form-group"><label>Description</label><textarea id="st_desc" rows="2">${isEdit ? escapeHtml(study.description || "") : ""}</textarea></div>
      <div class="form-group"><label>Sites investigateurs</label><div>${sitesHtml}</div></div>
    </div>`,
    `<button class="btn btn-secondary" id="stCancel">Annuler</button><button class="btn btn-primary" id="stSave">${isEdit ? "Enregistrer" : "Créer"}</button>`
  );
  $("#stCancel")!.addEventListener("click", closeModal);
  $("#stSave")!.addEventListener("click", async () => {
    const name = ($("#st_name") as HTMLInputElement).value.trim();
    if (!name) return notify("error", "Le nom est requis");
    const description = ($("#st_desc") as HTMLTextAreaElement).value.trim();
    const siteCodes = $$<HTMLInputElement>(".study-site-cb:checked").map((cb) => cb.value);
    const species = ($("#st_species") as HTMLSelectElement).value as "animal" | "human";
    try {
      if (isEdit) {
        await studiesApi.update(study.id, { name, description: description || undefined, siteCodes, species });
      } else {
        await studiesApi.create({ name, description: description || undefined, siteCodes, species });
      }
    } catch (err) {
      notify("error", err instanceof Error ? err.message : "Erreur");
      return;
    }
    closeModal();
    void renderAdminView(root);
  });
}
